import { type AxiosPromise } from "axios";
import { FileDropzone } from "components/FileUpload/FileDropzone";
import React from "react";
import { Progress } from "semantic-ui-react";
import { type UploadFilesConfig } from "utilities/fileUploadUtils";
import RelatedFiles from "./RelatedFiles";

interface Props {
  relatedFilesApi: () => AxiosPromise<server.dto.RelatedFiles>
  uploadFilesApi: (files: FileList | File[], config: UploadFilesConfig) => AxiosPromise<server.dto.FileUploadResult[]> | string
  onFilesSelected: (files: server.dto.File[]) => void
  initialSelectedFiles: server.dto.File[]
}

interface UploadStatus {
  fileNames: string[]
  percentage: number
}

const RelatedFilesWithUpload = (props: Props) => {
  const [uploadError, setUploadError] = React.useState<string | undefined>(undefined);
  const [uploadStatus, setUploadStatus] = React.useState<UploadStatus | undefined>(undefined);
  const [, setDropzoneText] = React.useState<string>(RESX.File.DropFilesHere);
  const [relatedFiles, setRelatedFiles] = React.useState<server.dto.RelatedFiles | undefined>(undefined);

  const updateUploadPercentage = (percent: number) => {
    setUploadStatus(prevState => ({
      fileNames: prevState.fileNames,
      percentage: percent
    }));
  };

  const onFileDrop = async (files: FileList | File[]) => {
    if (files.length === 0) {
      return;
    }

    const fileNames = Array.from(files).map(file => file.name);

    setUploadStatus({
      fileNames,
      percentage: 0
    });

    const config: UploadFilesConfig = {
      onUploadProgress: (e: ProgressEvent) => {
        const percentCompleted = Math.round((e.loaded * 100) / e.total);
        updateUploadPercentage(percentCompleted);
      },
      fileType: "any"
    };

    const uploadFileRes = props.uploadFilesApi(files, config);

    if (typeof uploadFileRes === "string") {
      setUploadError(uploadFileRes);
      setUploadStatus(undefined);
    } else {
      await uploadFileRes;
      const fetchFilesRes = await props.relatedFilesApi();
      setRelatedFiles(fetchFilesRes.data);
      setUploadError(undefined);
      setUploadStatus(undefined);
      if (window.filesUpdate) {
        window.filesUpdate();
      }
    }
  };

  if (uploadStatus) {
    return uploadStatus && <>
      <div className="flex justify-center align-center grow1">
        <div>
          <span className="s1_graysplashlogo"></span>
        </div>
      </div>

      <div>
        {uploadStatus.fileNames.join(", ")}
        <div>
          <Progress percent={uploadStatus.percentage} color="green" inverted progress active />
        </div>
      </div>
    </>;
  }

  return <RelatedFiles
    relatedFilesApi={props.relatedFilesApi}
    onFilesSelected={props.onFilesSelected}
    initialSelectedFiles={props.initialSelectedFiles}
    relatedFiles={relatedFiles}
    onRelatedFilesChanged={setRelatedFiles}
    error={uploadError}
    validFileTypes={"any"}
  >
    <div
      className="margin-top"
      onMouseOver={() => { setDropzoneText(RESX.File.ChooseFiles); }}
      onMouseOut={() => { setDropzoneText(RESX.File.DropFilesHere); }}
    >
      <FileDropzone
        onDrop={onFileDrop}
        onDropReject={(fileRejections) => {
          setUploadError(fileRejections[0].errors[0].message);
        }}
        maxSizeBytes={serverReference.constants.file.maximumClientFileContentLength}
        iconType="file"
      />
    </div>
  </RelatedFiles>;
};

export default RelatedFilesWithUpload;